import _define_property from "@swc/helpers/src/_define_property.mjs";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { EventNames, TrackingEventTypes, useAnalytics } from "@cinch-nx/shared-analytics";
import cx from "classnames";
import Link from "next/link";
import Head from "next/head";
import { usePagination } from "./hooks";
import styles from "./pagination.module.css";
var ellipses = "...";
// eslint-disable-next-line @typescript-eslint/no-empty-function
var noOp = function noOp() {
// this does nothing.
};
var Pagination = function(param) {
    var activePage = param.activePage, itemsCountPerPage = param.itemsCountPerPage, totalItemsCount = param.totalItemsCount, sourcePage = param.sourcePage, _onChange = param.onChange, onChange = _onChange === void 0 ? noOp : _onChange, _enabledLinks = param.enabledLinks, enabledLinks = _enabledLinks === void 0 ? false : _enabledLinks, _includedQueryParams = param.includedQueryParams, includedQueryParams = _includedQueryParams === void 0 ? "" : _includedQueryParams;
    // Guessing this is so we can wait for API calls to resolve
    // However this is maybe irrelevant if we're server-side fetching
    // So lets revisit this once the API is hooked up
    var currentPage = activePage || 1;
    var itemsPerPage = itemsCountPerPage || 24;
    var items = usePagination({
        currentPage: currentPage,
        itemsPerPage: itemsPerPage,
        totalItemsCount: totalItemsCount
    }).items;
    var trackEvent = useAnalytics().trackEvent;
    var handleClick = function() {
        trackEvent({
            eventName: "".concat(sourcePage, " - Pagination click"),
            type: TrackingEventTypes.ADOBE,
            data: {
                event: {
                    name: EventNames.click,
                    category: "page bottom",
                    label: "pagination",
                    action: EventNames.click
                }
            }
        });
    };
    var isFirstPage = currentPage === 1;
    var isLastPage = currentPage === items[items.length - 1] || items.length === 0;
    var isMultipleEllipses = items.filter(function(item) {
        return item === ellipses;
    }).length > 1;
    var renderPrevNextButtons = function(direction, isDisabled) {
        var isNext = direction === "next";
        var ariaLabel = isNext ? "Next page" : "Previous page";
        var pageNumber = isNext ? currentPage + 1 : currentPage - 1;
        var pageHref = pageNumber === 1 ? "/".concat(sourcePage) : "/".concat(sourcePage, "/page/").concat(pageNumber);
        return enabledLinks ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Head, {
                    children: [
                        !isFirstPage && direction === "previous" ? /*#__PURE__*/ _jsx("link", {
                            rel: "prev",
                            href: pageHref
                        }, "next-link-prev") : null,
                        !isLastPage && direction === "next" ? /*#__PURE__*/ _jsx("link", {
                            rel: "next",
                            href: pageHref
                        }, "next-link-next") : null
                    ]
                }),
                /*#__PURE__*/ _jsx(Link, {
                    href: isDisabled ? "" : "".concat(pageHref).concat(includedQueryParams),
                    passHref: true,
                    children: /*#__PURE__*/ _jsx("a", {
                        "aria-disabled": isDisabled,
                        href: pageHref,
                        "aria-label": ariaLabel,
                        onClick: function(e) {
                            return !isDisabled ? onChange(pageNumber) : e.preventDefault();
                        },
                        className: cx(styles.button, styles.nextPrevButton),
                        children: isNext ? "Next" : "Prev"
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsx("button", {
            "aria-disabled": isDisabled,
            disabled: isDisabled,
            onClick: function() {
                return !isDisabled ? onChange(pageNumber) : null;
            },
            "aria-label": ariaLabel,
            className: cx(styles.button, styles.nextPrevButton),
            children: isNext ? "Next" : "Prev"
        });
    };
    var renderPageNumberButton = function(pageNumber) {
        var isLastItem = pageNumber === items[items.length - 1];
        var ariaLabel = isLastItem ? "Page ".concat(pageNumber, ", last page") : "Page ".concat(pageNumber);
        var pageHref = pageNumber === 1 ? "/".concat(sourcePage) : "/".concat(sourcePage, "/page/").concat(pageNumber);
        return enabledLinks ? /*#__PURE__*/ _jsx(Link, {
            href: "".concat(pageHref).concat(includedQueryParams),
            passHref: true,
            children: /*#__PURE__*/ _jsx("a", {
                href: pageHref,
                "aria-label": ariaLabel,
                "aria-current": activePage === pageNumber ? "page" : undefined,
                "data-testid": "page-number-button",
                className: cx(styles.button, styles.pageNumberButton, _define_property({}, styles.pageNumberButtonActive, activePage === pageNumber)),
                onClick: function(e) {
                    return onChange(pageNumber);
                },
                children: pageNumber.toLocaleString()
            })
        }) : /*#__PURE__*/ _jsx("button", {
            "aria-label": ariaLabel,
            "aria-current": activePage === pageNumber ? "page" : undefined,
            "data-testid": "page-number-button",
            onClick: function() {
                return onChange(pageNumber);
            },
            className: cx(styles.button, styles.pageNumberButton, _define_property({}, styles.pageNumberButtonActive, activePage === pageNumber)),
            children: pageNumber.toLocaleString()
        });
    };
    return /*#__PURE__*/ _jsx("nav", {
        "aria-label": "Pagination",
        className: styles.pagination,
        "data-testid": "pagination",
        children: /*#__PURE__*/ _jsxs("ul", {
            onClick: handleClick,
            className: cx(styles.list, _define_property({}, styles.listWithMultipleEllipses, isMultipleEllipses)),
            children: [
                /*#__PURE__*/ _jsx("li", {
                    className: styles.listItem,
                    children: renderPrevNextButtons("previous", isFirstPage)
                }),
                items.length > 0 && items.map(function(pageNumber, index) {
                    return pageNumber === ellipses ? /*#__PURE__*/ _jsx("li", {
                        className: cx(styles.listItem, styles.ellipses),
                        children: "…"
                    }, "".concat(pageNumber, "-").concat(index)) : /*#__PURE__*/ _jsx("li", {
                        className: styles.listItem,
                        children: renderPageNumberButton(Number(pageNumber))
                    }, pageNumber);
                }),
                /*#__PURE__*/ _jsx("li", {
                    className: styles.listItem,
                    children: renderPrevNextButtons("next", isLastPage)
                })
            ]
        })
    });
};
export default Pagination;
